export const PAGINATION_ELLIPSIS = 'ellipsis' as const;

export type PaginationItem = number | typeof PAGINATION_ELLIPSIS;

/**
 * Builds a compact page list with ellipsis when `total` is large.
 * Example (page 5 of 63): [1, 'ellipsis', 4, 5, 6, 'ellipsis', 63]
 */
export function getPaginationItems(
  page: number,
  total: number,
  siblingCount = 1,
): PaginationItem[] {
  if (total <= 0) return [];
  if (total === 1) return [1];

  const maxVisible = siblingCount * 2 + 5;
  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const range = (start: number, end: number) =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, total);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leadingCount = 3 + 2 * siblingCount;
    return [...range(1, leadingCount), PAGINATION_ELLIPSIS, total];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const trailingCount = 3 + 2 * siblingCount;
    return [1, PAGINATION_ELLIPSIS, ...range(total - trailingCount + 1, total)];
  }

  if (showLeftEllipsis && showRightEllipsis) {
    return [
      1,
      PAGINATION_ELLIPSIS,
      ...range(leftSibling, rightSibling),
      PAGINATION_ELLIPSIS,
      total,
    ];
  }

  return range(1, total);
}
